import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Play, Square, SkipBack, SkipForward, Repeat, Gauge, Lock, Crown,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { formatTimeSeconds } from '../audio/audioCodec';

// Saved-recording player with seek bar, speed (PRO), and A/B loop (PRO).
// Only mounts when showAdvancedPlayer is true (parent already gates).

const AdvancedPlayerCard = ({
  isPremium,
  currentTime,
  duration,
  playbackSpeed,
  loopEnabled,
  setLoopEnabled,
  loopA,
  loopB,
  onSeek,
  onSpeedChange,
  onSetA,
  onSetB,
  onResetLoop,
  onStopPlayback,
}) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className="bg-[#121212] border border-amber/30 rounded-2xl p-6 mb-8"
  >
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-heading text-lg font-bold text-white flex items-center gap-2">
        <Play className="w-5 h-5 text-amber" />
        Player Avanzato
      </h3>
      <Button
        variant="ghost"
        size="sm"
        onClick={onStopPlayback}
        className="text-zinc-400 hover:text-white"
      >
        <Square className="w-4 h-4 mr-1" />
        Stop
      </Button>
    </div>

    {/* Seek Bar + Times */}
    <div className="space-y-2 mb-6">
      <div className="flex items-center gap-4">
        <span className="text-amber font-mono text-sm w-14">{formatTimeSeconds(currentTime)}</span>
        <input
          type="range"
          min="0"
          max={duration || 100}
          value={currentTime}
          onChange={(e) => onSeek(parseFloat(e.target.value))}
          className="flex-1 h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-amber"
          data-testid="seek-bar"
        />
        <span className="text-zinc-400 font-mono text-sm w-14">{formatTimeSeconds(duration)}</span>
      </div>
    </div>

    {/* Speed Control (PRO Only) */}
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-2">
        <Gauge className={`w-4 h-4 ${isPremium ? 'text-purple-400' : 'text-zinc-600'}`} />
        <span className="text-zinc-400 text-sm">Velocità</span>
        {!isPremium && <Lock className="w-3 h-3 text-zinc-600 ml-1" />}
      </div>
      <div className="flex gap-2">
        {[0.5, 0.75, 1.0, 1.25, 1.5].map((speed) => (
          <Button
            key={speed}
            onClick={() => onSpeedChange(speed)}
            disabled={!isPremium}
            size="sm"
            className={`px-3 ${
              playbackSpeed === speed
                ? 'bg-purple-500 text-white'
                : isPremium
                  ? 'bg-zinc-700 text-white hover:bg-zinc-600'
                  : 'bg-zinc-800 text-zinc-600 cursor-not-allowed'
            }`}
            data-testid={`speed-${speed}`}
          >
            {speed}x
          </Button>
        ))}
      </div>
      {!isPremium && (
        <p className="text-zinc-600 text-xs mt-1 flex items-center gap-1">
          <Crown className="w-3 h-3" /> Solo PRO
        </p>
      )}
    </div>

    {/* Loop A/B (PRO Only) */}
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Repeat className={`w-4 h-4 ${isPremium ? 'text-green-400' : 'text-zinc-600'}`} />
        <span className="text-zinc-400 text-sm">Loop A/B</span>
        {!isPremium && <Lock className="w-3 h-3 text-zinc-600 ml-1" />}
      </div>

      {isPremium ? (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Button
              onClick={onSetA}
              size="sm"
              className="bg-blue hover:bg-blue/80 text-white"
              data-testid="set-point-a"
            >
              <SkipBack className="w-4 h-4 mr-1" />
              Set A
            </Button>
            <span className="text-blue font-mono text-sm min-w-16">
              {loopA !== null ? formatTimeSeconds(loopA) : '--:--'}
            </span>

            <Button
              onClick={onSetB}
              disabled={loopA === null}
              size="sm"
              className="bg-green-500 hover:bg-green-500/80 text-white disabled:opacity-50"
              data-testid="set-point-b"
            >
              <SkipForward className="w-4 h-4 mr-1" />
              Set B
            </Button>
            <span className="text-green-400 font-mono text-sm min-w-16">
              {loopB !== null ? formatTimeSeconds(loopB) : '--:--'}
            </span>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => setLoopEnabled(!loopEnabled)}
              disabled={loopA === null || loopB === null}
              size="sm"
              className={`${
                loopEnabled
                  ? 'bg-green-500 text-white'
                  : 'bg-zinc-700 text-zinc-300'
              } disabled:opacity-50`}
              data-testid="toggle-loop"
            >
              <Repeat className="w-4 h-4 mr-1" />
              Loop {loopEnabled ? 'ON' : 'OFF'}
            </Button>

            <Button
              onClick={onResetLoop}
              variant="ghost"
              size="sm"
              className="text-zinc-400 hover:text-white"
              data-testid="reset-loop"
            >
              Reset A/B
            </Button>
          </div>
        </div>
      ) : (
        <div className="bg-zinc-800/50 rounded-lg p-4 text-center">
          <Lock className="w-6 h-6 text-zinc-600 mx-auto mb-2" />
          <p className="text-zinc-500 text-sm mb-2">Loop A/B disponibile solo per PRO</p>
          <Link to="/premium">
            <Button size="sm" className="bg-gradient-to-r from-amber to-orange-500 text-black font-semibold">
              <Crown className="w-4 h-4 mr-1" />
              Upgrade
            </Button>
          </Link>
        </div>
      )}
    </div>
  </motion.div>
);

export default AdvancedPlayerCard;
